import { Button, IconButton } from "@chakra-ui/button";
import { Input } from "@chakra-ui/input";
import { Heading, HStack, VStack } from "@chakra-ui/layout";
import { Flex, Spacer } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { InputControl } from "formik-chakra-ui";
import React from "react";
import { RiAddCircleLine, RiDeleteBin6Line } from "react-icons/ri";
import { NavLink } from "react-router-dom";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import {
  generateKey,
  isUserGeneratedKey,
} from "src/scenes/orgAdmin/formTemplates/ContentTab/V2/autoGenerateKey";
import { validateWithZod } from "src/services/formValidations";
import * as Url from "src/services/url";
import { OrganizationSelect } from "../components/OrganizationSelect";
import {
  GradeFormType,
  GradesConfigFormSchema,
  GradesConfigFormType,
  GradesConfigType,
  toGradesConfig,
} from "./types";

interface GradesConfigFormProps {
  initialGradesConfig?: GradesConfigType;
  onSubmit: (values: GradesConfigType) => void;
  submitting: boolean;
}

const emptyGrade: GradeFormType = {
  label: "",
  order: "",
  value: "",
  userGeneratedValue: false,
};

export const GradesConfigForm: React.FC<GradesConfigFormProps> = ({
  initialGradesConfig,
  onSubmit,
  submitting,
}) => {
  const initialValues: GradesConfigFormType = React.useMemo(
    () => ({
      organizationId: initialGradesConfig?.organizationId ?? "",
      grades: initialGradesConfig?.grades.map((grade) => ({
        id: grade.id ?? undefined,
        label: grade.label ?? "",
        order: grade.order?.toString() ?? "",
        value: grade.value ?? "",
        userGeneratedValue: isUserGeneratedKey(grade.value, grade.label),
      })) ?? [emptyGrade],
    }),
    [initialGradesConfig]
  );

  const handleSubmit = React.useCallback(
    (values: GradesConfigFormType) => {
      onSubmit(toGradesConfig(values));
    },
    [onSubmit]
  );

  return (
    <Formik<GradesConfigFormType>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validateWithZod(GradesConfigFormSchema)}
      enableReinitialize
    >
      {(formik) => (
        <Flex direction="column" minHeight="100%" as={Form} noValidate>
          <VStack align="left" spacing={8}>
            <Heading as="h1" variant="admin">
              {initialGradesConfig ? "Edit grades" : "Create grades"}
            </Heading>

            <OrganizationSelect
              organizationId={formik.values.organizationId}
              onChange={(organizationId) => {
                formik.setFieldValue("organizationId", organizationId ?? "");
              }}
              disabled={initialGradesConfig !== undefined}
              error={
                formik.submitCount > 0
                  ? formik.errors.organizationId
                  : undefined
              }
            />

            <Heading as="h2" size="md" variant="admin">
              Grades configuration
            </Heading>
            <VStack gap={4}>
              {formik.values.grades.map((grade, index) => (
                <HStack
                  width="100%"
                  align="end"
                  key={index}
                  gap={2}
                  alignItems="flex-start"
                >
                  <InputControl
                    name={`grades.${index}.label`}
                    label="Grade label"
                    isRequired
                    labelProps={{ requiredIndicator: <></> }}
                    inputProps={{
                      onChange: async (event) => {
                        const label = event.target.value;
                        const valueName = `grades.${index}.value`;
                        const shouldChangeValue =
                          formik.values.grades[index]?.userGeneratedValue ===
                          false;

                        await formik.setFieldValue(
                          `grades.${index}.label`,
                          label,
                          false
                        );
                        if (shouldChangeValue) {
                          await formik.setFieldValue(
                            valueName,
                            generateKey(label),
                            false
                          );
                        }
                      },
                    }}
                  >
                    <Input type="text" placeholder="E.g.: First grade" />
                  </InputControl>
                  <InputControl
                    name={`grades.${index}.value`}
                    label="Key"
                    isRequired
                    labelProps={{ requiredIndicator: <></> }}
                    inputProps={{
                      onChange: async (event) => {
                        const value = event.target.value;
                        const userGeneratedValue = value.trim().length > 0;
                        await formik.setFieldValue(
                          `grades.${index}.value`,
                          value,
                          false
                        );
                        await formik.setFieldValue(
                          `grades.${index}.userGeneratedValue`,
                          userGeneratedValue,
                          false
                        );
                      },
                    }}
                  >
                    <Input type="text" placeholder="E.g.: reference key" />
                  </InputControl>
                  <InputControl
                    name={`grades.${index}.order`}
                    label="Order"
                    isRequired
                    labelProps={{ requiredIndicator: <></> }}
                  >
                    <Input type="number" placeholder="E.g.: 1" />
                  </InputControl>
                  <IconButton
                    type="button"
                    aria-label="Remove program"
                    icon={<RiDeleteBin6Line />}
                    marginTop="1.8rem"
                    onClick={() => {
                      const newGrades = [...formik.values.grades];
                      newGrades.splice(index, 1);
                      formik.setFieldValue("grades", newGrades);
                    }}
                    variant="outline"
                    isDisabled={grade.id !== undefined}
                  />
                </HStack>
              ))}
              <Button
                variant="ghost"
                size="sm"
                colorScheme="gray"
                alignSelf="flex-end"
                type="button"
                aria-label="Add program"
                leftIcon={<RiAddCircleLine />}
                onClick={() => {
                  formik.setFieldValue("grades", [
                    ...formik.values.grades,
                    emptyGrade,
                  ]);
                }}
              >
                Add grade
              </Button>
            </VStack>
          </VStack>
          <Spacer minH={8} />
          <AdminFormButtons justifyContent="space-between">
            <Button
              as={NavLink}
              to={Url.Admin.GradesConfig.index()}
              variant="outline"
              colorScheme="gray"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              marginLeft={4}
              isLoading={submitting}
              isDisabled={!formik.dirty}
            >
              Submit
            </Button>
          </AdminFormButtons>
        </Flex>
      )}
    </Formik>
  );
};
